import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PasswordRecoveryPage from './pages/PasswordRecoveryPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ClientSelectionPage from './pages/ClientSelectionPage';
import SwitchUserPage from './pages/SwitchUserPage';
import DashboardPage from './pages/DashboardPage';
import UserManagementPage from './pages/UserManagementPage';
import ClientManagementPage from './pages/ClientManagementPage';
import DocumentTypesPage from './pages/DocumentTypesPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import APIManagement from './pages/APIManagement/APIManagement';
import ApplicationDetail from './pages/APIManagement/ApplicationDetail';
import LogsPage from './pages/Logs/Logs';
import Analytics from './pages/Analytics/Analytics';
import RoleManagementPage from './pages/RoleManagement/RoleManagementPage';
import SuperAdminPortal from './pages/SuperAdmin/SuperAdminPortal';
import BillingPage from './pages/BillingPage';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuthStore } from './store/authStore';

const { Content } = Layout;

function App() {
  const { isAuthenticated, user, selectedClient } = useAuthStore();

  // Helper function to determine where authenticated users should go
  const getDefaultRoute = () => {
    if (!isAuthenticated) return "/login";
    
    // Super admins go directly to super admin portal
    if (user?.role === 'super_admin') {
      return "/super-admin/dashboard";
    }
    
    // Everyone else (company admins and users) go to dashboard
    return "/dashboard";
  };

  return (
    <Layout className="layout-container">
      <Content>
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <LoginPage />} 
          />
          <Route 
            path="/register" 
            element={isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <RegisterPage />} 
          />
          <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitationPage />} />
          
          {/* Switch user route - accessible to authenticated users */}
          <Route 
            path="/switch-user" 
            element={
              <ProtectedRoute>
                <SwitchUserPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/client-selection" 
            element={
              <ProtectedRoute>
                <ClientSelectionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute requiredPermissions={['user_management:read', 'user_management:write']}>
                <UserManagementPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/roles" 
            element={
              <ProtectedRoute requiredPermissions={['user_management:write']}>
                <RoleManagementPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/clients" 
            element={
              <ProtectedRoute requiredPermissions={['client_management:read']}>
                <ClientManagementPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/document-types" 
            element={
              <ProtectedRoute requiredPermissions={['settings:write']}>
                <DocumentTypesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/api-management" 
            element={
              <ProtectedRoute requiredPermissions={['api_management:read']}>
                <APIManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/api-management/applications/:appId" 
            element={
              <ProtectedRoute requiredPermissions={['api_management:read']}>
                <ApplicationDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/logs" 
            element={
              <ProtectedRoute requiredPermissions={['logs:read']}>
                <LogsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute requiredPermissions={['analytics:read']}>
                <Analytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/billing" 
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/role-management" 
            element={
              <ProtectedRoute requiredPermissions={['user_management:write']}>
                <RoleManagementPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Super Admin Portal */}
          <Route 
            path="/super-admin/*" 
            element={
              <ProtectedRoute requiredRole="super_admin">
                <SuperAdminPortal />
              </ProtectedRoute>
            } 
          />
          
          {/* Default redirect */}
          <Route 
            path="/" 
            element={<Navigate to={getDefaultRoute()} />} 
          />
          <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
        </Routes>
      </Content>
    </Layout>
  );
}

export default App;
